import React, { useState, useEffect, useMemo } from 'react';
import ApiService from '../../services/api';
import { NotClockedInTeacher, TeacherInfo, TeacherProfile, ClockedInEmployee } from '../../types';
import { useStaffStatus } from '../../contexts/StaffStatusContext';
import * as ApiTypes from '../../types/api';

import './NotClockedInTeachers.css';
import { useStaffData } from '../../data/staffData';

interface NotClockedInTeachersProps {
  refreshTrigger?: number; // Optional prop to trigger refresh
}

const NotClockedInTeachers: React.FC<NotClockedInTeachersProps> = ({ refreshTrigger }) => {
  // Get staff status context to report missing clockins
  const { setMissingClockins } = useStaffStatus();
  
  const [notClockedInTeachers, setNotClockedInTeachers] = useState<NotClockedInTeacher[]>([]);

  // States for logout functionality
  const [loggingOutTeachers, setLoggingOutTeachers] = useState<Record<string, boolean>>({});
  const [logoutErrors, setLogoutErrors] = useState<Record<string, string>>({});
  const [logoutSuccess, setLogoutSuccess] = useState<string[]>([]);

  const staffData = useStaffData().data;
  const signedInTeachers = useMemo(() => staffData?.smartcareCheckedInStaff || [], [staffData]);
  const clockedInEmployees = useMemo(() => staffData?.wheniworkClockedinStaff || [], [staffData]);

  // Process data to find teachers not clocked in
  useEffect(() => {
    // Find teachers who are signed in SmartCare but not clocked in WhenIWork
    const notClockedIn = signedInTeachers
      .filter(({ profile }) => (
        !clockedInEmployees.some(
          employee => employee.employee_code === profile.customId ||
          (employee.first_name === profile.firstName && employee.last_name === profile.lastName)
        )
      ))
      .filter(({ profile }) => profile.lastName != 'Siegfried') // do not list chris as he doesn't punch in
      .map(({ employee, profile }) => ({
        firstName: employee.firstName,
        lastName: employee.lastName,
        smartcareId: employee.guid,
        customId: profile.customId,
        signedInClass: employee.className
      }));

    setNotClockedInTeachers(notClockedIn);
    
    // Update the context with the count of missing clockins
    setMissingClockins(notClockedIn.length);
  }, [signedInTeachers, clockedInEmployees, setMissingClockins]);

  const handleLogoutTeacher = async (teacherId: string) => {
    try {
      // Set loading state for this specific teacher
      setLoggingOutTeachers(prev => ({ ...prev, [teacherId]: true }));
      
      // Call API to log out teacher
      const result = await ApiService.logoutTeacher(teacherId);
      
      if (result.success) {
        // Add to success list
        setLogoutSuccess(prev => [...prev, teacherId]);
        
        // Remove from not clocked in list
        setNotClockedInTeachers(prev => 
          prev.filter(teacher => teacher.smartcareId !== teacherId)
        );
        
        // Clear any previous error for this teacher
        setLogoutErrors(prev => {
          const updated = { ...prev };
          delete updated[teacherId];
          return updated;
        });
      } else {
        // Set error for this teacher
        setLogoutErrors(prev => ({ 
          ...prev, 
          [teacherId]: result.message || 'Failed to sign out teacher' 
        }));
      }
    } catch (error) {
      // Extract error message from the error
      const errorMessage = error instanceof Error ? error.message : 'Error signing out teacher';
      
      // Set error for this teacher
      setLogoutErrors(prev => ({ 
        ...prev, 
        [teacherId]: errorMessage 
      }));
    } finally {
      // Clear loading state for this teacher
      setLoggingOutTeachers(prev => {
        const updated = { ...prev };
        delete updated[teacherId];
        return updated;
      });
    }
  };

  // Use either external or internal loading/error states
  const isLoading = useStaffData().isLoading;
  const error = useStaffData().error

  return (
    <div className="not-clocked-in-teachers-component not-clocked-in-section">
      <h2>Missing When I Work Clockins</h2>
      {isLoading ? (
        <p className="loading-message">Loading...</p>
      ) : error ? (
        <p className="error-message">{error.message}</p>
      ) : notClockedInTeachers.length > 0 ? (
        <ul className="not-clocked-in-list">
          {notClockedInTeachers.map(teacher => (
            <li key={teacher.smartcareId} className="not-clocked-in-teacher">
              <div className="teacher-info">
                <span className="teacher-name">{`${teacher.firstName} ${teacher.lastName}`}</span>
                {teacher.signedInClass && (
                  <span className="teacher-location">
                    {` - Signed into ${teacher.signedInClass}`}
                  </span>
                )}
              </div>
              <div className="teacher-actions">
                {logoutSuccess.includes(teacher.smartcareId) ? (
                  <span className="logout-success">Logged out</span>
                ) : (
                  <button 
                    className="logout-button"
                    onClick={() => handleLogoutTeacher(teacher.smartcareId)}
                    disabled={loggingOutTeachers[teacher.smartcareId] || false}
                  >
                    {loggingOutTeachers[teacher.smartcareId] ? 'Logging out...' : 'Log Out of SmartCare'}
                  </button>
                )}
                {logoutErrors[teacher.smartcareId] && (
                  <span className="logout-error">{logoutErrors[teacher.smartcareId]}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="empty-message">All teachers are properly clocked in</p>
      )}
    </div>
  );
};

export default NotClockedInTeachers;
